import * as React from "react";
import {Splitter} from "../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import {useEffect, useState} from "react";
import {backend} from "../../../../xconvert-backend";
import {authentication} from "../../../../authentication";
import ReactTable from "react-table";
import "react-table/react-table.css";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import {ConfigContext} from "../../context/ConfigContext";
import {CompanyNameAndId} from "../../../../generated/api";
import { Popup, Label } from "semantic-ui-react";

export function SchedulerReportConfigs() {

    const context = React.useContext(ConfigContext);

    // State for all configs
    const [schedulerReportConfigs, setSchedulerReportConfigs] = useState([]);
    // State for selected config
    const [selectedConfig, setSelectedConfig] = useState<any | null>(null);
    // State für editierbare Config und Dirty-Check
    const [editConfig, setEditConfig] = useState<any | null>(null);
    const [isDirty, setIsDirty] = useState(false);

    const [companyIdToNameMapping, setCompanyIdToNameMapping] = useState<CompanyNameAndId[]>([]);

    useEffect(() => {
        async function getCompanyNames(companyIds: string[]) {
            let auth = backend.withTokenAuthHeader(authentication.token);
            let resp = await backend.internalApi.getCompanyNames(companyIds.join(","), auth);
            return resp.companyNames || [];
        }

        loadConfigs().then((configs) => {

            let companyIds = configs.map((c: any) => c.companyId)
            getCompanyNames(companyIds).then((cNames) => {
                setCompanyIdToNameMapping(cNames);
            })

        })
    }, []);

    useEffect(() => {
        setEditConfig(selectedConfig ? {...selectedConfig} : null);
        setIsDirty(false);
    }, [selectedConfig]);

    async function loadConfigs() {
        let auth = backend.withTokenAuthHeader(authentication.token);
        let resp = await backend.internalApi.fetchAllSchedulerReportingConfigurations(auth);
        setSchedulerReportConfigs(resp.configurations || []);
        // Reset selection if configs change
        setSelectedConfig(null);
        return resp.configurations || [];
    }

    async function saveConfig() {
        if (!editConfig) return;
        let auth = backend.withTokenAuthHeader(authentication.token);
        await backend.internalApi.updateSchedulerReportingConfiguration(selectedConfig._id, editConfig, auth);
        await loadConfigs();
        // Nach dem Speichern die neue Config selektieren
        setSelectedConfig(editConfig);
    }

    function onAceChange(newValue: string) {
        try {
            const parsed = JSON.parse(newValue);
            setEditConfig(parsed);
            setIsDirty(JSON.stringify(parsed) !== JSON.stringify(selectedConfig));
        } catch (e) {
            // Bei JSON Fehler nicht dirty setzen
        }
    }

    async function runNow() {
        if (!editConfig) return;
        const now = new Date().toISOString();
        const updated = {...editConfig, nextExecution: now};
        setEditConfig(updated);
        setIsDirty(true);
        // Optional: direkt speichern
        let auth = backend.withTokenAuthHeader(authentication.token);
        await backend.internalApi.updateSchedulerReportingConfiguration(selectedConfig._id, updated, auth);
        await loadConfigs();
        setSelectedConfig(updated);
    }



    function drawTable() {
        const columns = [
            {
                Header: 'State',
                accessor: 'state',
                width: 80,
                Cell: (row: any) => {
                    const value = row.value;
                    let color = value === 'OK' ? '#52c41a' : value === 'FAIL' ? '#ff4d4f' : undefined;
                    return (
                        <span style={{
                            background: color ? color : undefined,
                            color: color ? 'white' : undefined,
                            borderRadius: 4,
                            padding: '2px 8px',
                            display: 'inline-block',
                            minWidth: 50,
                            textAlign: 'center',
                        }}>{value}</span>
                    );
                }
            },
            {
                Header: 'ID',
                accessor: '_id',
                width: 210,
            },
            {
                Header: 'Display Name',
                accessor: 'displayName',
                width: 200,
            },
            {
                Header: 'Company ID',
                accessor: 'companyId',
                width: 200,
                Cell: (row: any) => {
                    const companyId = row.value;
                    const company = companyIdToNameMapping.find(c => c.id === companyId);
                    const companyName = company ? company.name : undefined;
                    return companyName ? (
                        <Popup
                            content={companyName}
                            trigger={<Label color="blue" basic style={{cursor: 'pointer'}}>{companyId}</Label>}
                            position="top left"
                        />
                    ) : (
                        <Label basic>{companyId}</Label>
                    );
                }
            },
            {
                Header: 'Next Execution',
                accessor: 'nextExecution',
                width: 180,
                Cell: (row: any) => row.value ? (
                    <span style={{fontWeight: 'bold'}}>{new Date(row.value).toLocaleString()}</span>
                ) : '',
            },
            {
                Header: 'Last Execution',
                accessor: 'lastExecution',
                width: 180,
                Cell: (row: any) => row.value ? new Date(row.value).toLocaleString() : '',
            },
            {
                Header: 'Report Name',
                accessor: 'reportName',
                width: 160,
            },
            {
                Header: 'Error Message',
                accessor: 'errorMessage',
                width: 200,
                Cell: (row: any) => row.value || '',
            },
        ];
        // Aktuelle companyId aus context holen
        // @ts-ignore
        const currentCompanyId = context?.companyId;
        return (
            <ReactTable
                data={schedulerReportConfigs}
                columns={columns}
                defaultPageSize={50}
                className="-striped -highlight"
                getTrProps={(state: any, rowInfo: any) => {
                    let isSelected = selectedConfig && rowInfo && selectedConfig._id === rowInfo.original._id;
                    let isCurrentCompany = rowInfo && rowInfo.original && rowInfo.original.companyId === currentCompanyId;
                    return {
                        onClick: () => {
                            if (rowInfo && rowInfo.original) {
                                setSelectedConfig(rowInfo.original);
                            }
                        },
                        style: {
                            cursor: 'pointer',
                            background: isSelected
                                ? '#e6f7ff'
                                : isCurrentCompany
                                    ? '#fffbe6' // gelbliches Highlight für gleiche companyId
                                    : undefined
                        }
                    };
                }}
            />
        );
    }

    function drawDetails() {
        if (!selectedConfig || !editConfig) {
            return <div style={{padding: 16, color: '#888'}}>Select a configuration to view details.</div>;
        }
        return (
            <div style={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column'}}>
                <div style={{marginBottom: 8, display: 'flex', gap: 8}}>
                    <button
                        onClick={saveConfig}
                        disabled={!isDirty}
                        style={{padding: '6px 16px', fontWeight: 500, background: isDirty ? '#1890ff' : '#eee', color: isDirty ? 'white' : '#888', border: 'none', borderRadius: 4, cursor: isDirty ? 'pointer' : 'not-allowed'}}
                    >
                        Save
                    </button>
                    <button
                        onClick={runNow}
                        style={{padding: '6px 16px', fontWeight: 500, background: '#faad14', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer'}}
                    >
                        Run now
                    </button>
                </div>
                <div style={{flex: 1, minHeight: 0}}>
                    <AceEditor
                        mode="json"
                        theme="monokai"
                        value={JSON.stringify(editConfig, null, 2)}
                        onChange={onAceChange}
                        width="100%"
                        height="100%"
                        setOptions={{useWorker: false}}
                    />
                </div>
            </div>
        );
    }

    return (
        <Splitter
            name={"SchedulerReportConfigsSplitter"}
            left={drawTable()}
            right={drawDetails()}
        />
    );
}